import React, { useState } from 'react';
import axios from 'axios';
import './POImport.css';
import API_BASE_URL from '../config';
import POImportResults from './POImportResults';

function POImport() {
  const [file, setFile] = useState(null);
  const [googleSheetUrl, setGoogleSheetUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [importResults, setImportResults] = useState(null);
  const [importType, setImportType] = useState('file'); // 'file' or 'sheet'

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Check file type
      const validTypes = [
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/csv'
      ];

      if (validTypes.includes(selectedFile.type) ||
          selectedFile.name.endsWith('.xlsx') ||
          selectedFile.name.endsWith('.xls') ||
          selectedFile.name.endsWith('.csv')) {
        setFile(selectedFile);
        setError('');
      } else {
        setError('Please select a valid Excel (.xlsx, .xls) or CSV file');
        setFile(null);
      }
    }
  };

  const handleFileUpload = async () => {
    if (!file) {
      setError('Please select a file first');
      return;
    }

    setLoading(true);
    setError('');

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API_BASE_URL}/api/po/import`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        setImportResults(response.data);
        setError('');
      } else {
        setError(response.data.error || 'Failed to import file');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to connect to server');
      console.error('Import error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSheetImport = async () => {
    if (!googleSheetUrl.trim()) {
      setError('Please enter a Google Sheets URL');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API_BASE_URL}/api/po/import-from-sheet`, {
        sheetUrl: googleSheetUrl
      });

      if (response.data.success) {
        setImportResults(response.data);
        setError('');
      } else {
        setError(response.data.error || 'Failed to import from Google Sheets');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to connect to server');
      console.error('Import error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="po-import-container">
      <div className="po-import-card">
        <h2>📥 PO Import</h2>
        <p className="subtitle">Import PO data from Excel, CSV, or Google Sheets</p>

        {/* Import Type Tabs */}
        <div className="import-tabs">
          <button
            className={`tab ${importType === 'file' ? 'active' : ''}`}
            onClick={() => setImportType('file')}
          >
            📄 File Upload
          </button>
          <button
            className={`tab ${importType === 'sheet' ? 'active' : ''}`}
            onClick={() => setImportType('sheet')}
          >
            📊 Google Sheets
          </button>
        </div>

        {/* File Upload */}
        {importType === 'file' && (
          <div className="import-section">
            <div className="file-input-group">
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileChange}
                disabled={loading}
                id="file-upload"
              />
              <label htmlFor="file-upload" className="file-label">
                {file ? file.name : 'Choose Excel or CSV file...'}
              </label>
            </div>

            <button
              onClick={handleFileUpload}
              disabled={loading || !file}
              className="btn-primary"
            >
              {loading ? '⏳ Importing...' : '📤 Import File'}
            </button>

            <div className="info-box">
              <p><strong>Supported formats:</strong> Excel (.xlsx, .xls), CSV (.csv)</p>
              <p><strong>Required columns:</strong> รหัส, วันที่สั่งซื้อ, ลูกค้า, รถยนต์, รุ่น, ทะเบียน, บริษัทประกัน, etc.</p>
            </div>
          </div>
        )}

        {/* Google Sheets */}
        {importType === 'sheet' && (
          <div className="import-section">
            <div className="input-group">
              <input
                type="text"
                placeholder="Paste Google Sheets URL here..."
                value={googleSheetUrl}
                onChange={(e) => setGoogleSheetUrl(e.target.value)}
                disabled={loading}
                className="sheet-url-input"
              />
            </div>

            <button
              onClick={handleGoogleSheetImport}
              disabled={loading || !googleSheetUrl.trim()}
              className="btn-primary"
            >
              {loading ? '⏳ Importing...' : '📊 Import from Google Sheets'}
            </button>

            <div className="info-box">
              <p><strong>How to use:</strong></p>
              <ol>
                <li>Open your Google Sheet</li>
                <li>Click "Share" → "Anyone with the link can view"</li>
                <li>Copy the sheet URL and paste it above</li>
              </ol>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="error-message">
            ❌ {error}
          </div>
        )}
      </div>

      {/* Import Results */}
      {importResults && (
        <POImportResults
          results={importResults}
          onClose={() => setImportResults(null)}
        />
      )}
    </div>
  );
}

export default POImport;
